import {
  Check,
  Publish,
  SaveOutlined,
  Straighten,
  type SvgIconComponent,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Typography,
  type MenuItemProps,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStudio } from '../../context';
import type { DesignEdit__Design as Design } from '../DesignEdit.generated';

interface FileMenuItemProps extends MenuItemProps {
  icon: SvgIconComponent;
  label: string;
  endAdornment?: React.ReactNode;
}

const FileMenuItem = ({ icon: Icon, label, endAdornment, ...props }: FileMenuItemProps) => (
  <MenuItem {...props} sx={{ py: 1.5, px: 2, gap: 1.5 }}>
    <Icon fontSize="small" />
    <Typography sx={{ flex: 1 }}>{label}</Typography>
    {endAdornment}
  </MenuItem>
);

interface StudioFileMenuProps {
  design: Design;
  onSave: () => void;
  saveLoading: boolean;
  onPublish: () => void;
  publishLoading: boolean;
}

export const StudioFileMenu = ({
  design,
  onSave,
  saveLoading,
  onPublish,
  publishLoading,
}: StudioFileMenuProps) => {
  const { t } = useTranslation();
  const { dirty, showRulers, setShowRulers } = useStudio();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSave = useCallback(() => {
    onSave();
    handleClose();
  }, [onSave, handleClose]);

  const handlePublish = useCallback(() => {
    onPublish();
    handleClose();
  }, [onPublish, handleClose]);

  const handleToggleRulers = useCallback(() => {
    setShowRulers(!showRulers);
    handleClose();
  }, [showRulers, setShowRulers, handleClose]);

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          color: 'text.primary',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 14,
          px: 1.5,
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {t('pages.studio.edit.file_menu.file')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: { minWidth: 280 },
          },
        }}
      >
        <Box px={2} py={1.5}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {design.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {design.width} × {design.height}px
          </Typography>
        </Box>

        <Divider sx={{ my: '0 !important' }} />

        <FileMenuItem
          onClick={handleToggleRulers}
          icon={showRulers ? Check : Straighten}
          label={t('pages.studio.edit.file_menu.show_rulers')}
        />

        <Divider sx={{ my: '0 !important' }} />

        <FileMenuItem
          onClick={handleSave}
          disabled={!dirty || saveLoading}
          icon={SaveOutlined}
          label={t('common.actions.save')}
          endAdornment={
            <Typography variant="caption" color="text.secondary">
              {saveLoading
                ? t('pages.studio.edit.file_menu.saving')
                : dirty
                ? t('pages.studio.edit.file_menu.unsaved_changes')
                : t('pages.studio.edit.file_menu.all_changes_saved')}
            </Typography>
          }
        />

        <FileMenuItem
          onClick={handlePublish}
          disabled={saveLoading || publishLoading}
          icon={Publish}
          label={t('pages.studio.edit.toolbar.publish')}
        />
      </Menu>
    </>
  );
};
